import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	Image,
	styled,
	Text,
	TextTag,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { getEtiquetaDgt } from '../../helpers/ofertas-helper';
import {
	CarlineWithOfertas,
	Oferta,
	Promocion,
} from '../../services/ofertas-service/models/ofertas';
import { OfertaCardAccordionCta } from './oferta-CTAs';
import { OfertaPromotionLabel } from './oferta-promotion-label';

const MotorizacionContainer = styled.div<{ numberOfOfertas: number }>`
	padding-top: ${(props) => props.theme.size.static100};

	@media screen and (max-width: 559px) {
		max-width: 255px;
		min-height: ${(props) => props.numberOfOfertas > 1 && '40px'};
		text-wrap: auto;
	}

	@media screen and (min-width: 560px) and (max-width: 759px) {
		max-width: 415px;
		min-height: ${(props) => props.numberOfOfertas > 1 && '40px'};
		text-wrap: auto;
	}

	@media screen and (min-width: 760px) and (max-width: 959px) {
		max-width: 630px;
		min-height: ${(props) => props.numberOfOfertas > 1 && '50px'};
	}
`;

const StyledImage = styled(Image)`
	width: 24px;
	height: 24px;

	@media screen and (min-width: 960px) {
		width: 28px;
		height: 28px;
	}
`;

export const OfertaCardTitle = ({
	carline,
	isLayerVisible,
	setIsLayerVisible,
	currentOferta,
	selectedOferta,
	setSelectedOferta,
	handleLayerOnClick,
	setSelectedPromotion,
}: {
	className?: string;
	carline: CarlineWithOfertas;
	isMobile: boolean;
	isLayerVisible: boolean;
	setIsLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	currentOferta: Oferta;
	selectedOferta: Oferta;
	setSelectedOferta: React.Dispatch<React.SetStateAction<Oferta>>;
	handleLayerOnClick: () => void;
	setSelectedPromotion: React.Dispatch<
		React.SetStateAction<Promocion | undefined>
	>;
}): React.ReactElement => {
	return (
		<Container
			data-testid="oferta-card-title"
			wrap={'never'}
			horizontalAlign={'space-between'}
			shrinkContent
		>
			<Container
				data-testid="title"
				wrap={'always'}
				horizontalAlign={'space-between'}
				verticalAlign={'center'}
				stretchContent
			>
				<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
					{currentOferta.promociones && (
						<OfertaPromotionLabel
							handleLayerOnClick={handleLayerOnClick}
							promociones={currentOferta.promociones}
							setSelectedPromotion={setSelectedPromotion}
						/>
					)}
				</BreakpointWrapper>
				<BreakpointWrapper min={Breakpoints.b960}>
					{selectedOferta &&
						selectedOferta.id === currentOferta.id &&
						currentOferta.promociones && (
							<OfertaPromotionLabel
								handleLayerOnClick={handleLayerOnClick}
								promociones={currentOferta.promociones}
								setSelectedPromotion={setSelectedPromotion}
							/>
						)}
				</BreakpointWrapper>
				<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
					<Container
						data-testid="title-dgt-mobile"
						verticalAlign={'center'}
						gutter={ContainerGutter.static200}
						padding={{
							bottom: ContainerPadding.static100,
							right: ContainerPadding.static520,
						}}
					>
						<Text
							tag={TextTag.h3}
							appearance={TokenTextAppearance.headline400}
							bold
						>
							{carline.nuevo ? 'Nuevo ' : ''}
							{currentOferta.carline?.salesgroup.title}
						</Text>
						{selectedOferta.configuracion.etiquetaDGT && (
							<StyledImage
								src={
									currentOferta.configuracion.etiquetaDGT &&
									getEtiquetaDgt(currentOferta.configuracion.etiquetaDGT)
								}
								alt={carline.title}
								width={24}
								height={24}
							/>
						)}
					</Container>
				</BreakpointWrapper>
				<BreakpointWrapper min={Breakpoints.b960}>
					{currentOferta.id === selectedOferta.id && (
						<Container
							verticalAlign={'center'}
							gutter={ContainerGutter.static200}
							padding={{ bottom: ContainerPadding.static100 }}
						>
							<Text
								tag={TextTag.h3}
								appearance={TokenTextAppearance.headline400}
								bold
							>
								{carline.nuevo ? 'Nuevo ' : ''}
								{currentOferta.carline?.salesgroup.title}
							</Text>
							{selectedOferta.configuracion.etiquetaDGT && (
								<Text>
									<StyledImage
										src={
											currentOferta.configuracion.etiquetaDGT &&
											getEtiquetaDgt(currentOferta.configuracion.etiquetaDGT)
										}
										alt={carline.title}
										width={24}
										height={24}
									/>
								</Text>
							)}
						</Container>
					)}
				</BreakpointWrapper>

				<MotorizacionContainer
					className="motorizacion-container"
					numberOfOfertas={carline.ofertas.length}
				>
					<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
						<Text appearance={TokenTextAppearance.label200} wordBreak>
							{currentOferta.configuracion.motorizacion.name}
						</Text>
					</BreakpointWrapper>
					<BreakpointWrapper min={Breakpoints.b960}>
						{currentOferta.id === selectedOferta.id ? (
							<Text appearance={TokenTextAppearance.label200} wordBreak>
								{currentOferta.configuracion.motorizacion.name}
							</Text>
						) : (
							<Text appearance={TokenTextAppearance.label200} wordBreak>
								{currentOferta.carline?.salesgroup.title}{' '}
								{currentOferta.configuracion.motorizacion.name}
							</Text>
						)}
					</BreakpointWrapper>
				</MotorizacionContainer>
			</Container>

			{selectedOferta && selectedOferta.id === currentOferta.id && (
				<BreakpointWrapper
					min={Breakpoints.b960}
					data-testid="selected-oferta-cta"
				>
					<Container
						data-testid="oferta-card-accordion-cta"
						wrap={'never'}
						horizontalAlign={'flex-end'}
						verticalAlign={'flex-end'}
						stretchContent
					>
						<OfertaCardAccordionCta
							currentOferta={currentOferta}
							selectedOferta={selectedOferta}
							setSelectedOferta={setSelectedOferta}
							isLayerVisible={isLayerVisible}
							setIsLayerVisible={setIsLayerVisible}
						/>
					</Container>
				</BreakpointWrapper>
			)}
		</Container>
	);
};
